import { Action, Reducer } from 'redux';
import { IPublicClientApplication } from '@azure/msal-browser';
import * as TeamsState from './TeamsState';
import { TokenHelper } from '../TokenHelper';
import { AppThunkAction } from '.';
import { SortHelper } from '../SortHelper';
import { Customer } from '../interfaces/Customer';
import { ContactCustomerRole } from '../interfaces/ContactCustomerRole';
import { KnownDataAction } from './Data';
import { KnownContactsAction } from './contacts';
import { KnownConsultantAction } from './consultants';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface CustomersState {
    customers: Customer[] | undefined;
    customerEdit: Customer | null;
    customerContactsEdit: Customer | null;
    customersLoading: boolean;
    customerQuickView: Customer | null;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

interface ReloadCustomersAction { type: 'RELOAD_CUSTOMERS' }
interface RequestCustomersAction { type: 'REQUEST_CUSTOMERS' }
interface ReceiveCustomersAction { type: 'RECEIVE_CUSTOMERS'; customers: Customer[]; }
interface RequestCustomerContactsAction { type: 'REQUEST_CUSTOMERCONTACTS'; customerid: string; }
interface ReceiveCustomerContactsAction { type: 'RECEIVE_CUSTOMERCONTACTS'; customerid: string; contactCustomerRoles:ContactCustomerRole[] }
interface EditCustomersAction { type: 'EDIT_CUSTOMER'; customer: Customer|null; }
interface EditCustomerContactsAction { type: 'EDIT_CUSTOMERCONTACTS'; customer: Customer|null; }
interface PatchCustomerAction { type: 'PATCH_CUSTOMER'; customer: Customer; }
interface QuickViewCustomerAction { type: 'QV_CUSTOMER'; customer: Customer | null; }


// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownCustomersAction = ReloadCustomersAction | RequestCustomersAction | ReceiveCustomersAction | EditCustomersAction | EditCustomerContactsAction | PatchCustomerAction | RequestCustomerContactsAction | ReceiveCustomerContactsAction | QuickViewCustomerAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const customersActionCreators = {
    requestCustomers: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownCustomersAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_CUSTOMERS' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.data) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Customers`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<Customer[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_CUSTOMERS', customers: data });
                            });
                    }
                });
        }
    },
    requestCustomerContacts: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined, customerid:string): AppThunkAction<KnownCustomersAction> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.data) {
            dispatch({ type: 'REQUEST_CUSTOMERCONTACTS', customerid: customerid });
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Customers/` + customerid + "/Contacts", {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ContactCustomerRole[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_CUSTOMERCONTACTS', customerid: customerid, contactCustomerRoles:data });
                            });
                    }
                });
        }
    },
    ReloadCustomer: (): AppThunkAction<KnownCustomersAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RELOAD_CUSTOMERS'});
    },
    requestCustomerEdition: (customer: Customer|null): AppThunkAction<KnownCustomersAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_CUSTOMER', customer: customer });
    },
    requestPatchCustomerContacts: (customerid: string, contacts:ContactCustomerRole[]): AppThunkAction<KnownCustomersAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RECEIVE_CUSTOMERCONTACTS', customerid: customerid, contactCustomerRoles: contacts });
    },
    requestCustomerContactsEdition: (customer: Customer|null): AppThunkAction<KnownCustomersAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_CUSTOMERCONTACTS', customer: customer });
    },
    patchCustomerEdition: (customer: Customer): AppThunkAction<KnownCustomersAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_CUSTOMER', customer: customer });
    },
    requestCustomerQuickView: (customer: Customer | null): AppThunkAction<KnownCustomersAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_CUSTOMER', customer: customer });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducerCustomer: Reducer<CustomersState> = (state: CustomersState | undefined, incomingAction: Action): CustomersState => {
    if (state === undefined) {
        return {
            customers: undefined, customersLoading: false, customerEdit: null, customerContactsEdit: null, customerQuickView: null,
        };
    }

    const action = incomingAction as KnownCustomersAction;
    switch (action.type) {
        case 'RELOAD_CUSTOMERS':
            return { ...state, customers: undefined };
        case 'REQUEST_CUSTOMERS':
            return { ...state, customersLoading:true };
        case 'RECEIVE_CUSTOMERS':
            return { ...state, customersLoading: false, customers: action.customers.sort(SortHelper.CustomerByName) };
        case 'EDIT_CUSTOMER':
            return { ...state, customerEdit: action.customer };
        case 'EDIT_CUSTOMERCONTACTS':
            return { ...state, customerContactsEdit: action.customer };
        case 'PATCH_CUSTOMER': {
            const ns = { ...state };
            if (ns.customers && action.customer.customerid) {
                const c = ns.customers.findIndex(e => e.customerid === action.customer.customerid);
                if (c !== -1) {
                    ns.customers[c] = action.customer;
                    // If quickView element is the one we update, force update
                    if (state.customerQuickView && state.customerQuickView.customerid === action.customer.customerid) {
                        ns.customerQuickView = { ...ns.customers[c] };
                    }
                    if (state.customerContactsEdit && state.customerContactsEdit.customerid === action.customer.customerid) {
                        ns.customerContactsEdit = { ...ns.customers[c] };
                    }
                }
                else {
                    ns.customers.push(action.customer);
                }
                ns.customers = [...ns.customers.sort(SortHelper.CustomerByName)];
                return ns;
            }
            return state;
        }
        case 'REQUEST_CUSTOMERCONTACTS': {
            const ns = { ...state };
            if (ns.customers) {
                const c = ns.customers.findIndex(e => e.customerid === action.customerid);
                if (c !== -1) {
                    ns.customers[c].contactsLoading = true;
                    // If quickView element is the one we update, force update
                    if (state.customerQuickView && state.customerQuickView.customerid === action.customerid) {
                        ns.customerQuickView = { ...ns.customers[c] };
                    }
                    if (state.customerContactsEdit && state.customerContactsEdit.customerid === action.customerid) {
                        ns.customerContactsEdit = { ...ns.customers[c] };
                    }
                    ns.customers = [...ns.customers.sort(SortHelper.CustomerByName)];
                    return ns;
                }
            }
            return state;
        }
        case 'RECEIVE_CUSTOMERCONTACTS': {
            const ns = { ...state };
            if (ns.customers) {
                const c = ns.customers.findIndex(e => e.customerid === action.customerid);
                if (c !== -1) {
                    ns.customers[c].contactsLoading = false;
                    ns.customers[c].contacts = action.contactCustomerRoles;
                    // If quickView element is the one we update, force update
                    if (state.customerQuickView && state.customerQuickView.customerid === action.customerid) {
                        ns.customerQuickView = { ...ns.customers[c] };
                    }
                    if (state.customerContactsEdit && state.customerContactsEdit.customerid === action.customerid) {
                        ns.customerContactsEdit = { ...ns.customers[c] };
                    }
                    ns.customers = [...ns.customers.sort(SortHelper.CustomerByName)];
                    return ns;
                }
            }
            return state;
        }
        case 'QV_CUSTOMER': {
            return { ...state, customerQuickView: action.customer };
        }
        default: {
            break;
        }
    }
    // For the QuickView is selected on an other, clear this one
    const actionData = incomingAction as KnownDataAction;
    switch (actionData.type) {
        case 'EDIT_PROJECTCONTACTS':
            if (actionData.project === null) return { ...state, customerContactsEdit: null };
            break;
        case 'QV_CLEAR':
        case 'QV_INVOICE':
        case 'QV_PROJECT':
        case 'QV_PROJECTASSIGMENT':
        case 'QV_TIMESHEET':
        case 'QV_PRODUCT': {
            return { ...state, customerQuickView: null };
        }
        default: {
            break;
        }
    }
    const actionContact = incomingAction as KnownContactsAction;
    switch (actionContact.type) {
        case 'QV_CONTACT': {
            return { ...state, customerQuickView: null };
        }
        default: {
            break;
        }
    }
    const actionConsultant = incomingAction as KnownConsultantAction;
    switch (actionConsultant.type) {
        case 'QV_CONSULTANT': {
            return { ...state, customerQuickView: null };
        }
        default: {
            break;
        }
    }
    return state;
};